import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {blogPagination, BLOG_PAGE_SIZE} from '~/lib/blogPagination';
import {blogListingSeo} from '~/lib/blogSeo.server';
import type {LinksFunction} from '@shopify/remix-oxygen';
import {
  LEGACY_BLOG_CATEGORIES,
  legacyRedirectTarget,
} from '~/lib/legacyRedirects';
//domains
import {
  getLangagueCodeForSanityQuery,
  getCountryCodeForSanityQuery,
} from '~/domains/sanity';
import {
  useBlogCategoriesPageQuery as fetchCategoryBlogs,
  useBlogCategoryQuery as fetchCategory,
} from 'domains/blog';

//components
import BlogListing from '~/components/blogListing';

//translation
import translation from 'translations/blog.json';
import {routeLocale} from 'domains/global';

export const headers = routeHeaders;

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({request, params, context}: LoaderArgs) {
  if (!params.categoryHandle) throw new Response(null, {status: 404});

  const langPrefix = params.lang ? `/${params.lang}` : '';

  // This redirect was written but its return value was discarded, so
  // /blogs/all quietly rendered a second copy of /blogs at its own URL.
  if (params.categoryHandle === 'all') {
    throw redirect(`${langPrefix}/blogs`, 301);
  }

  // Shopify's old blog handles carry most of the store's historical inbound
  // links. There is no Sanity category to land on, so send them to the index
  // rather than 404.
  if (LEGACY_BLOG_CATEGORIES.has(params.categoryHandle)) {
    throw redirect(`${langPrefix}/blogs`, 301);
  }

  const {page, start, end, redirectTo} = blogPagination(request.url);
  if (redirectTo) return redirect(redirectTo, 301);

  const {currentCategory} = await fetchCategory({
    languageCode: getLangagueCodeForSanityQuery(
      context.storefront.i18n.language,
    ),
    categoryHandle: params.categoryHandle,
  });

  // invariant(currentTag, `Tag doesn't exist`);
  if (!currentCategory) {
    const legacy = legacyRedirectTarget(new URL(request.url).pathname);
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  const {blogs, recentBlogs, tags, totalBlogs, categories, blogSettings} =
    await fetchCategoryBlogs({
      languageCode: getLangagueCodeForSanityQuery(
        context.storefront.i18n.language,
      ),
      countryCode: getCountryCodeForSanityQuery(
        context.storefront.i18n.country,
      ),
      start,
      end,
      categoryHandle: params.categoryHandle,
    });
  // An empty first page is valid; later empty pages must not become soft 404s.
  if (page > 1 && blogs.length === 0) {
    throw new Response(null, {status: 404});
  }
  const {seo, heading, description} = blogListingSeo({
    requestUrl: request.url,
    english: context.storefront.i18n.language === 'EN',
    page,
    subject: currentCategory.category_title,
    blogs,
  });

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return json(
    {
      translation: translation[selectedLocale],

      seo,
      heading,
      description,
      page,
      pageCount: Math.ceil(totalBlogs / BLOG_PAGE_SIZE),
      blogs,
      recentBlogs,
      tags,
      totalBlogs,
      categories,
      blogSettings,
      currentTag: null,
      currentCategory,
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  const data = useLoaderData<typeof loader>();
  return <BlogListing {...data} />;
}
